import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { PORTFOLIO_ASSETS } from "@/data/content";
import { pickLocale } from "@/i18n";

const PRIMARY_ASSET_SLUGS = [
  "berriver-long-bien",
  "thang-long-tower",
  "dong-anh-industrial",
  "chitta-center-hanoi",
  "chitta-hoa-binh",
];

export default function PortfolioAssets() {
  useReveal();
  const { t } = useTranslation();

  const assets = PRIMARY_ASSET_SLUGS
    .map((slug) => PORTFOLIO_ASSETS.find((a) => a.slug === slug))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <PageHero
        eyebrow={t("investments.heroEyebrow") + " / " + t("investments.subnav.portfolioAssets")}
        title={t("investments.portfolioTitle")}
        accent={<><br/>{t("investments.portfolioSubtitle")}</>}
        sub={t("investments.portfolioBody")}
        bgImage="/assets/zv2i0yeu_Long-Bien-Barrier.jpg"
      />

      <section className="relative bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {assets.map((a, i) => (
              <Link
                key={a.slug}
                to={`/asset/${a.slug}`}
                data-reveal
                style={{ "--reveal-delay": `${i * 90}ms` }}
                className="group block bg-white border border-[#14355a]/10 hover:border-[#14355a]/40 transition-all duration-500 hover:-translate-y-1"
                data-testid={`asset-card-${a.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#14355a]/5">
                  <img
                    src={a.img}
                    alt={pickLocale(a, "name") || a.name}
                    className="w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-105"
                  />
                </div>
                <div className="p-8 lg:p-10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="font-display text-[#14355a] font-medium tracking-[0.15em]" style={{ fontSize: "clamp(1.75rem, 2.4vw, 2.5rem)", lineHeight: 1 }}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <ArrowUpRight
                      size={22}
                      className="text-[#14355a]/40 group-hover:text-unik-gold group-hover:rotate-12 transition-all duration-500"
                    />
                  </div>
                  <div className="mt-6 eyebrow text-[#14355a]">{pickLocale(a, "type") || a.type}</div>
                  <h3
                    style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.75rem)", fontWeight: 500 }}
                    className="mt-4 font-display text-[#14355a] tracking-tight"
                  >
                    {pickLocale(a, "name") || a.name}
                  </h3>
                  <p className="mt-4 text-[16px] lg:text-[17px] text-unik-ink/70 leading-[1.7]">{pickLocale(a, "location") || a.location}</p>
                  <div className="mt-7 pt-7 border-t border-[#14355a]/10 grid grid-cols-1 gap-6 text-[15px] lg:text-[16px]">
                    <div>
                      <div className="text-unik-ink/55 uppercase tracking-wider text-[11px] font-medium">{t("investments.labels.scale")}</div>
                      <div className="mt-2 text-[#14355a]" style={{ fontWeight: 500 }}>{pickLocale(a, "scale") || a.scale}</div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow={t("investments.bottomCta.eyebrow")}
        title={t("investments.bottomCta.title")}
        accent={t("investments.bottomCta.accent")}
        body={t("investments.bottomCta.body")}
        primary={{ label: t("investments.bottomCta.primary"), to: "/contact" }}
        secondary={{ label: t("investments.bottomCta.secondary"), to: "/about/portfolio-companies" }}
      />

      <SiteFooter />
    </div>
  );
}
